import { gradeOptionLabels } from "../data/quick-sheets/filterOptionLabels";
import { getAllowedFilterIds } from "../data/quick-sheets/filterRegistry";
import { toFilterId } from "../utils/toFilterId";
import {
  MAX_LEARNING_OBJECTIVES_LENGTH,
  MAX_PASTED_CONTENT_LENGTH,
  MAX_REGENERATION_INSTRUCTIONS_LENGTH,
  MAX_TOPIC_LENGTH,
} from "./constants";

export const FILTER_FIELD_IDS = ["curriculum", "grade", "subject"];

export function getAllowedFilterIdsSnapshot() {
  const allowed = getAllowedFilterIds();
  return {
    curriculum: allowed.curriculum,
    grade: new Set(gradeOptionLabels.map(toFilterId)),
    subject: allowed.subject,
  };
}

export function normalizeCatalogValue(value) {
  return String(value ?? "")
    .trim()
    .replace(/\s+/g, " ")
    .toUpperCase();
}

export function fieldError(field, code, message) {
  return { field, code, message };
}

export function labelsToAllowedSet(labels) {
  return new Set(labels.map(normalizeCatalogValue));
}

export function optionLabelsToAllowedSet(options) {
  return new Set(options.map((option) => normalizeCatalogValue(option.label)));
}

export function mergeErrors(...groups) {
  return groups.flat();
}

export function validateFilterIds(filters, errors = {}) {
  const allowedFilterIds = getAllowedFilterIds();
  for (const field of FILTER_FIELD_IDS) {
    const value = filters?.[field];
    if (!value) {
      errors[field] = fieldError(field, "REQUIRED", `Please select a ${field}.`);
      continue;
    }
    if (!allowedFilterIds[field]?.has(value)) {
      errors[field] = fieldError(
        field,
        "UNSUPPORTED_VALUE",
        `Selected ${field} is not supported.`,
      );
    }
  }
  return errors;
}

export function validateCatalogLabel(field, value, allowedSet, errors = {}) {
  const cleaned = String(value ?? "").trim();
  if (!cleaned) {
    errors[field] = fieldError(field, "REQUIRED", `Please select a ${field}.`);
    return errors;
  }
  if (!allowedSet.has(normalizeCatalogValue(cleaned))) {
    errors[field] = fieldError(
      field,
      "UNSUPPORTED_VALUE",
      `${field} value is not supported.`,
    );
  }
  return errors;
}

export function validateStandardCurriculumBody(body, allowedSets, errors = {}) {
  validateCatalogLabel("curriculum", body.curriculum, allowedSets.curriculum, errors);
  validateCatalogLabel("grade", body.grade, allowedSets.grade, errors);
  validateCatalogLabel("subject", body.subject, allowedSets.subject, errors);
  return errors;
}

export function validateRequiredText(
  field,
  value,
  errors = {},
  {
    maxLength = MAX_TOPIC_LENGTH,
    requiredMessage,
  } = {},
) {
  const cleaned = String(value ?? "").trim();
  if (!cleaned) {
    errors[field] = fieldError(
      field,
      "REQUIRED",
      requiredMessage ?? `${field} is required.`,
    );
    return errors;
  }
  if (cleaned.length > maxLength) {
    errors[field] = fieldError(
      field,
      "MAX_LENGTH",
      `${field} must be at most ${maxLength} characters.`,
    );
  }
  return errors;
}

export function validateRequiredPositiveNumber(
  field,
  value,
  errors = {},
  {
    requiredMessage,
    minMessage,
    maxMessage,
    min = 1,
    max,
  } = {},
) {
  const num = Number(value);
  if (value == null || value === "" || !Number.isFinite(num)) {
    errors[field] = fieldError(
      field,
      "REQUIRED",
      requiredMessage ?? `${field} is required.`,
    );
    return errors;
  }
  if (num < min) {
    errors[field] = fieldError(
      field,
      "MIN_VALUE",
      minMessage ?? `${field} must be at least ${min}.`,
    );
    return errors;
  }
  if (max != null && num > max) {
    errors[field] = fieldError(
      field,
      "MAX_VALUE",
      maxMessage ?? `${field} must be at most ${max}.`,
    );
  }
  return errors;
}

export function validateOptionalText(
  field,
  value,
  errors = {},
  { maxLength = MAX_PASTED_CONTENT_LENGTH } = {},
) {
  const cleaned = String(value ?? "").trim();
  if (!cleaned) {
    return errors;
  }
  if (cleaned.length > maxLength) {
    errors[field] = fieldError(
      field,
      "MAX_LENGTH",
      `${field} must be at most ${maxLength} characters.`,
    );
  }
  return errors;
}

export function validateStringListLabels(
  field,
  values,
  allowedSet,
  errors = {},
  { minItems = 1, maxItems = 10 } = {},
) {
  const list = Array.isArray(values) ? values : [];
  if (list.length < minItems) {
    errors[field] = fieldError(
      field,
      "REQUIRED",
      `At least ${minItems} ${field} value(s) required.`,
    );
    return errors;
  }
  if (list.length > maxItems) {
    errors[field] = fieldError(
      field,
      "MAX_ITEMS",
      `At most ${maxItems} ${field} values allowed.`,
    );
    return errors;
  }
  list.forEach((value, index) => {
    const itemField = `${field}[${index}]`;
    const cleaned = String(value ?? "").trim();
    if (!cleaned) {
      errors[itemField] = fieldError(itemField, "REQUIRED", `${itemField} is required.`);
      return;
    }
    if (!allowedSet.has(normalizeCatalogValue(cleaned))) {
      errors[itemField] = fieldError(
        itemField,
        "UNSUPPORTED_VALUE",
        `${itemField} value is not supported.`,
      );
    }
  });
  return errors;
}

export function finalizeErrors(errors) {
  return {
    ok: Object.keys(errors).length === 0,
    errors,
  };
}

export function validateRegenerationInstructions(value) {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) {
    return { ok: true };
  }
  if (trimmed.length > MAX_REGENERATION_INSTRUCTIONS_LENGTH) {
    return {
      ok: false,
      error: fieldError(
        "regenerationInstructions",
        "MAX_LENGTH",
        `Instructions must be at most ${MAX_REGENERATION_INSTRUCTIONS_LENGTH} characters.`,
      ),
    };
  }
  return { ok: true };
}

export {
  MAX_LEARNING_OBJECTIVES_LENGTH,
  MAX_PASTED_CONTENT_LENGTH,
  MAX_REGENERATION_INSTRUCTIONS_LENGTH,
  MAX_TOPIC_LENGTH,
};
